import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DroppableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { GripVertical, Check, X, Plus, Trash2 } from 'lucide-react';
import { Input } from 'ui/shadcn/input';
import Button from 'ui/Button';
import { cn } from 'lib/utils';

export interface CategoryItem {
    id: string;
    name: string;
    order: number;
}

interface CategoryManagerProps {
    categories: CategoryItem[];
    onChange: (categories: CategoryItem[]) => void;
    placeholder?: string;
}

const IconButton: React.FC<{
    label: string;
    onClick: () => void;
    className?: string;
    children: React.ReactNode;
}> = ({ label, onClick, className, children }) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        onClick={onClick}
        className={cn(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-500 transition-colors duration-fast hover:bg-muted [&_svg]:h-4 [&_svg]:w-4',
            className
        )}
    >
        {children}
    </button>
);

const CategoryManager: React.FC<CategoryManagerProps> = ({
    categories,
    onChange,
    placeholder = '카테고리명 입력'
}) => {
    const [localCategories, setLocalCategories] = useState<CategoryItem[]>(categories);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [editingCategoryName, setEditingCategoryName] = useState('');

    useEffect(() => {
        setLocalCategories(categories);
    }, [categories]);

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;

        const items = Array.from(localCategories);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        const reorderedCategories = items.map((item, index) => ({
            ...item,
            order: index + 1
        }));

        setLocalCategories(reorderedCategories);
        onChange(reorderedCategories);
    };

    const addCategory = () => {
        if (!newCategoryName.trim()) {
            alert('카테고리명을 입력해주세요.');
            return;
        }

        const newCategory: CategoryItem = {
            id: `new-${Date.now()}`,
            name: newCategoryName.trim(),
            order: localCategories.length + 1
        };
        const updatedCategories = [...localCategories, newCategory];
        setLocalCategories(updatedCategories);
        onChange(updatedCategories);
        setNewCategoryName('');
    };

    const startEditCategory = (category: CategoryItem) => {
        setEditingCategoryId(category.id);
        setEditingCategoryName(category.name);
    };

    const saveEditCategory = () => {
        if (editingCategoryId && editingCategoryName.trim()) {
            const updatedCategories = localCategories.map(cat =>
                cat.id === editingCategoryId
                    ? { ...cat, name: editingCategoryName.trim() }
                    : cat
            );
            setLocalCategories(updatedCategories);
            onChange(updatedCategories);
            setEditingCategoryId(null);
            setEditingCategoryName('');
        }
    };

    const cancelEditCategory = () => {
        setEditingCategoryId(null);
        setEditingCategoryName('');
    };

    const deleteCategory = (categoryId: string) => {
        const updatedCategories = localCategories
            .filter(cat => cat.id !== categoryId)
            .map((cat, index) => ({ ...cat, order: index + 1 }));
        setLocalCategories(updatedCategories);
        onChange(updatedCategories);
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex gap-2">
                <Input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addCategory()}
                    className="flex-1"
                    placeholder={placeholder}
                />
                <Button type="button" variant="outline" onClick={addCategory} icon={<Plus />}>
                    추가
                </Button>
            </div>

            {localCategories.length > 0 && (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="categories">
                        {(provided: DroppableProvided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="flex flex-col gap-2">
                                {localCategories.map((category, index) => (
                                    <Draggable key={category.id} draggableId={category.id} index={index}>
                                        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                className={cn(
                                                    'flex h-10 items-center gap-3 rounded border px-3 transition-colors duration-fast',
                                                    snapshot.isDragging
                                                        ? 'border-navy-100 bg-navy-50'
                                                        : 'border-border bg-card'
                                                )}
                                            >
                                                <div
                                                    {...provided.dragHandleProps}
                                                    className="flex items-center text-neutral-400 hover:text-neutral-700"
                                                >
                                                    <GripVertical className="h-4 w-4" />
                                                </div>
                                                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neutral-100 px-1.5 text-xs font-medium tabular-nums text-neutral-700">
                                                    {index + 1}
                                                </span>
                                                {editingCategoryId === category.id ? (
                                                    <>
                                                        <Input
                                                            type="text"
                                                            value={editingCategoryName}
                                                            onChange={(e) => setEditingCategoryName(e.target.value)}
                                                            onKeyDown={(e) => e.key === 'Enter' && saveEditCategory()}
                                                            className="h-8 flex-1"
                                                            autoFocus
                                                        />
                                                        <IconButton label="확인" onClick={saveEditCategory} className="hover:text-success">
                                                            <Check />
                                                        </IconButton>
                                                        <IconButton label="취소" onClick={cancelEditCategory} className="hover:text-foreground">
                                                            <X />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span
                                                            className="flex-1 truncate text-base text-foreground cursor-pointer hover:text-primary"
                                                            title="클릭하여 이름 수정"
                                                            onClick={() => startEditCategory(category)}
                                                        >
                                                            {category.name}
                                                        </span>
                                                        <IconButton
                                                            label="삭제"
                                                            onClick={() => deleteCategory(category.id)}
                                                            className="hover:text-destructive"
                                                        >
                                                            <Trash2 />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            {localCategories.length === 0 && (
                <p className="rounded border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
                    카테고리를 추가해주세요
                </p>
            )}
        </div>
    );
};

export default CategoryManager;
